/**
 * Email Opened Event Component
 *
 * Handles ahoy_email_opened events.
 *
 * Display pattern:
 * - Header: "Email opened: {subject}" or "Email opened" when subject is missing
 * - Timestamp: uses opened_at from payload if available, falls back to event occurred_at
 */

import { bold, InlineText, TimelineEvent } from '../components/shared';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import type { EmailOpenedPayload } from '../types';
import { EVENT_TYPES } from '../types';
import type { EventComponentProps } from './types';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

function getCountryName(code: string): string {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

function formatEnrichmentLine(
  metadata: Record<string, unknown> | undefined
): string | null {
  if (!metadata || metadata.proxy_type === 'gmail') {
    return null;
  }

  const deviceType =
    typeof metadata.device_type === 'string' ? metadata.device_type : undefined;
  const emailClient =
    typeof metadata.email_client === 'string'
      ? metadata.email_client
      : undefined;
  const country =
    typeof metadata.country === 'string' ? metadata.country : undefined;

  let clientPart: string | null = null;
  if (deviceType && emailClient) {
    clientPart = `${deviceType} (${emailClient})`;
  } else if (deviceType) {
    clientPart = deviceType;
  } else if (emailClient) {
    clientPart = emailClient;
  }

  const countryPart = country ? `from ${getCountryName(country)}` : null;

  const parts = [clientPart, countryPart].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

export function EmailOpenedEvent({ event }: EventComponentProps) {
  const payload =
    event.event_type_name === EVENT_TYPES.EMAIL_OPENED
      ? (event.payload as EmailOpenedPayload | undefined)
      : undefined;

  const subject = payload?.subject;
  const timestamp = payload?.opened_at || event.occurred_at;
  const fromAddress = payload?.from;
  const toAddresses = payload?.to;

  const enrichmentLine = formatEnrichmentLine(event.metadata);

  const baseLabel = subject ? `Email opened: ${subject}` : 'Email opened';
  const ariaLabel = enrichmentLine
    ? `${baseLabel}, ${enrichmentLine}`
    : baseLabel;

  const metaTextClass = `${TIMELINE_DESIGN_TOKENS.typography.subItem} text-secondary`;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            {bold('Email opened')}
            {subject && <>: {subject}</>}
          </InlineText>
        }
        timestamp={timestamp}
      >
        {(fromAddress ||
          (toAddresses && toAddresses.length > 0) ||
          enrichmentLine) && (
          <div className='mt-2 flex flex-col gap-1'>
            {fromAddress && (
              <div className={metaTextClass}>From: {fromAddress}</div>
            )}
            {toAddresses && toAddresses.length > 0 && (
              <div className={metaTextClass}>To: {toAddresses.join(', ')}</div>
            )}
            {enrichmentLine && (
              <div className={metaTextClass}>{enrichmentLine}</div>
            )}
          </div>
        )}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
